import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCircle2 } from 'lucide-react';
import { paymentAPI } from '../../services/api';
import { formatDistanceToNow } from 'date-fns';

// Bell dropdown of recent payments actually received (status: completed) across the
// whole business — reuses the existing payments list (already sorted paymentDate desc,
// filtered server-side to completed only), no dedicated backend endpoint needed.
// "Unread" count is just "newer than last time the dropdown was opened", tracked in
// localStorage so it survives a refresh.
const LAST_SEEN_KEY = 'notif_last_seen_ts';
const DISMISSED_KEY = 'notif_dismissed_ids';
const DISMISSED_CAP = 200; // bound localStorage growth — old ids fall off the list first
const POLL_MS = 30000;

const readDismissed = () => {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY)) || []); } catch { return new Set(); }
};

const PAYMENT_FOR_LABELS = {
  broadband_renewal: 'Broadband Renewal',
  catv_renewal: 'CableTV Renewal',
  installation: 'Installation',
  security_deposit: 'Security Deposit',
  other: 'Payment',
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastSeen, setLastSeen] = useState(() => Number(localStorage.getItem(LAST_SEEN_KEY)) || 0);
  const [dismissed, setDismissed] = useState(readDismissed);
  const ref = useRef(null);

  // Several bills settled by one real-world payment (e.g. two skipped months paid together)
  // are still separate Payment docs — collapse the ones sharing a combinedGroupId (see
  // Payment.js) back into a single row, summing the amount, instead of showing e.g. one ₹800
  // payment as two ₹400 rows (2026-09-16 — SURAJSHINGH PARIHAR / A-73M).
  const collapseCombined = (payments) => {
    const groups = new Map();
    for (const p of payments) {
      const key = p.combinedGroupId || p._id;
      const existing = groups.get(key);
      if (!existing) { groups.set(key, { ...p, _combinedCount: 1 }); continue; }
      existing.totalAmount = (existing.totalAmount || 0) + (p.totalAmount || 0);
      existing._combinedCount += 1;
      if (new Date(p.paymentDate) > new Date(existing.paymentDate)) existing.paymentDate = p.paymentDate;
    }
    return [...groups.values()].sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await paymentAPI.getAll({ limit: 20, status: 'completed' });
      setItems(collapseCombined(data.data || []));
    } catch { /* silent — bell just stays empty on failure */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const visibleItems = items.filter(p => !dismissed.has(p._id));
  const unreadCount = visibleItems.filter(p => new Date(p.paymentDate).getTime() > lastSeen).length;

  const toggleOpen = () => {
    setOpen(o => {
      const next = !o;
      if (next) {
        const now = Date.now();
        localStorage.setItem(LAST_SEEN_KEY, String(now));
        setLastSeen(now);
      }
      return next;
    });
  };

  // Dismisses everything currently shown — re-opening later only shows payments that
  // arrive after this point, not ones already cleared. Capped so localStorage doesn't
  // grow forever across months of use.
  const clearAll = () => {
    const next = new Set([...dismissed, ...items.map(p => p._id)]);
    const capped = new Set([...next].slice(-DISMISSED_CAP));
    setDismissed(capped);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify([...capped]));
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={toggleOpen} title="Payment activity"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 6, borderRadius: 6, position: 'relative' }}>
        <Bell size={18} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2, minWidth: 15, height: 15, borderRadius: 8,
            background: 'var(--cb-dc2626)', color: 'var(--c-ffffff)', fontSize: '.62rem', fontWeight: 700, lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 8, width: 340,
          background: 'var(--bg-card, #fff)', border: '1px solid var(--border)', borderRadius: 10,
          boxShadow: '0 10px 30px rgba(0,0,0,.15)', zIndex: 300, maxHeight: 420, overflowY: 'auto',
        }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, fontSize: '.85rem' }}>Payments Received</span>
            {visibleItems.length > 0 && (
              <button onClick={clearAll} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: '.76rem', fontWeight: 600 }}>
                Clear
              </button>
            )}
          </div>
          {loading && visibleItems.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', fontSize: '.82rem', color: 'var(--text-muted)' }}>Loading…</div>
          ) : visibleItems.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', fontSize: '.82rem', color: 'var(--text-muted)' }}>No payments received yet</div>
          ) : (
            visibleItems.map(p => (
              <div key={p._id}
                onClick={() => { setOpen(false); if (p.customer?._id) navigate(`/customers/${p.customer._id}`); }}
                style={{ display: 'flex', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--border)', cursor: p.customer ? 'pointer' : 'default' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <CheckCircle2 size={16} color="var(--c-16a34a)" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '.82rem' }}>
                    <strong>Payment received</strong> — ₹{Math.round(p.totalAmount || 0).toLocaleString('en-IN')}
                    {p._combinedCount > 1 && ` (${p._combinedCount} bills)`}
                  </div>
                  <div style={{ fontSize: '.76rem', color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.customer?.name || 'Unknown customer'} · {p._combinedCount > 1 ? 'Multiple bills' : (PAYMENT_FOR_LABELS[p.paymentFor] || p.paymentFor)}
                  </div>
                  {p.customer?.address?.line1 && (
                    <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.customer.address.line1}
                    </div>
                  )}
                  <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginTop: 2 }}>
                    {formatDistanceToNow(new Date(p.paymentDate), { addSuffix: true })}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
